import type React from 'react';
import { useEffect, useMemo, useRef } from 'react';

import type { PermitProps } from 'hooks/permission/usePermission';
import usePermission from 'hooks/permission/usePermission';

import { logger } from 'utils/logger';
import { ERROR_REASONS } from 'utils/logger/transports/constants';

import NoPermissionFallback from './components/NoPermissionFallback';

interface Props extends PermitProps {
    children: React.ReactNode;
    withFallback?: boolean;
}

const PermissionWrapper = ({ children, withFallback = false, ...rest }: Props) => {
    const { getIsPermit } = usePermission();

    const FallbackScreen = () => {
        return withFallback ? <NoPermissionFallback /> : null;
    };

    const permit = getIsPermit({ ...rest });

    const prevPermitRef = useRef<boolean | null>(null);
    useEffect(() => {
        // A permit -> deny transition unmounts the wrapped page and wipes its
        // state. We only care about transitions away from 'permit', not the
        // initial null -> false (which is just route-load before permissions
        // arrive).
        if (prevPermitRef.current === true && !permit) {
            logger.warn('PermissionWrapper denied access after previously permitting', {
                tags: {
                    reason: ERROR_REASONS.permissionDeny,
                    permissionName: String(rest.name ?? ''),
                    permissionType: String(rest.type),
                    url: window.location.pathname,
                },
            });
        }
        prevPermitRef.current = permit;
    }, [permit, rest.name, rest.type]);

    const isContentPermit = useMemo(() => {
        if (permit) {
            return children;
        } else {
            return FallbackScreen();
        }
    }, [children, permit]);

    return <>{isContentPermit}</>;
};

export default PermissionWrapper;
